import React, { useEffect, useState } from 'react';
import { Alert, App, Form, Input, Modal, Select, Switch } from 'antd';
import { AdminUser, CreateUserInput, UpdateUserInput, UserRole } from './types';
import { useCreateUserMutation, useUpdateUserMutation } from './queries';
import { ApiError } from '../../api/types';

interface UserFormModalProps {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
  defaultRole?: UserRole;
}

interface FormValues {
  username: string;
  full_name?: string;
  email: string;
  password?: string;
  role: UserRole;
  statusSwitch: boolean;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  user,
  onClose,
  defaultRole = 'user',
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [modalError, setModalError] = useState<string | null>(null);

  const createMutation = useCreateUserMutation();
  const updateMutation = useUpdateUserMutation();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) {
      return;
    }
    setModalError(null);
    form.resetFields();
    if (user) {
      form.setFieldsValue({
        username: user.username,
        full_name: user.full_name ?? undefined,
        email: user.email,
        role: user.role,
        statusSwitch: user.status === 'active',
      });
    } else {
      form.setFieldsValue({ role: defaultRole, statusSwitch: true });
    }
  }, [open, user, form, defaultRole]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setModalError(null);
      const fullName = values.full_name?.trim();
      const status = values.statusSwitch ? 'active' : 'inactive';

      if (user) {
        const payload: UpdateUserInput = {
          username: values.username.trim(),
          full_name: fullName ? fullName : null,
          email: values.email.trim(),
          role: values.role,
          status,
        };
        const password = values.password?.trim();
        if (password) {
          payload.password = password;
        }
        await updateMutation.mutateAsync({ id: user.id, input: payload });
        message.success('User updated successfully');
      } else {
        const payload: CreateUserInput = {
          username: values.username.trim(),
          email: values.email.trim(),
          password: values.password ?? '',
          role: values.role,
          status,
        };
        if (fullName) {
          payload.full_name = fullName;
        }
        await createMutation.mutateAsync(payload);
        message.success('User created successfully');
      }
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) {
        // Antd form validation error, do not treat as API error
        return;
      }
      if (err instanceof ApiError) {
        setModalError(err.code ? `[${err.code}] ${err.message}` : err.message);
      } else if (err instanceof Error) {
        setModalError(err.message);
      } else {
        setModalError('Failed to save user. Please try again.');
      }
    }
  };

  return (
    <Modal
      title={user ? 'Edit user' : 'Create user'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={user ? 'Save changes' : 'Create'}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      destroyOnHidden
    >
      {modalError && (
        <Alert
          message={modalError}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setModalError(null)}
        />
      )}

      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true, message: 'Please enter a username' },
            { min: 3, message: 'Username must be at least 3 characters' },
            { max: 30, message: 'Username must be at most 30 characters' },
          ]}
        >
          <Input disabled={isSubmitting} autoComplete="off" />
        </Form.Item>

        <Form.Item name="full_name" label="Full Name" rules={[{ max: 100 }]}>
          <Input disabled={isSubmitting} autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter an email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input disabled={isSubmitting} autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="password"
          label={user ? 'New Password' : 'Password'}
          extra={user ? 'Leave blank to keep the current password' : undefined}
          rules={[
            {
              required: !user,
              message: 'Please enter a password',
            },
            { min: 8, message: 'Password must be at least 8 characters' },
            { max: 72, message: 'Password must be at most 72 characters' },
          ]}
        >
          <Input.Password
            disabled={isSubmitting}
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          name="role"
          label="Role"
          rules={[{ required: true, message: 'Please select a role' }]}
        >
          <Select
            disabled={isSubmitting}
            options={[
              { value: 'user', label: 'User' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
        </Form.Item>

        <Form.Item name="statusSwitch" label="Status" valuePropName="checked">
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            disabled={isSubmitting}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
